"use client"

import { useState } from "react"
import { Search, Bell, Mail } from "lucide-react"
import { ThemeToggle } from "./theme-toggle"
import { UserProfileDropdown } from "./user-profile-dropdown"
import { Breadcrumbs } from "./breadcrumbs"
import { ChatIconWithBadge } from "./chat-icon-with-badge"
import { useChat } from "@edemics/contexts/chat-context"
import { Popover, PopoverContent, PopoverTrigger } from "@edemics/ui/popover"
import { Button } from "@edemics/ui/button"
import { Badge } from "@edemics/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@edemics/ui/avatar"
import { X } from "lucide-react"
import { useIsInsideAppShellChrome } from "./app-shell-chrome-context"
import { useNotificationsPoll } from "@edemics/hooks/use-notifications-poll"

interface HeaderProps {
  onMenuToggle?: () => void
  sidebarCollapsed?: boolean
  /**
   * Ported parent-portal pages pass a handful of extra props that the
   * shell header doesn't render (the shell owns title/breadcrumbs via
   * <Breadcrumbs />). Accepted here as optionals so the ported call
   * sites type-check without touching page JSX.
   */
  title?: string
  currentModule?: string
  breadcrumbs?: Array<string | { label: string; href?: string }>
  onMenuClick?: () => void
  /** Alias for `onMenuToggle` accepted for legacy parent-portal callers. */
  toggleSidebar?: () => void
}

export function Header({ onMenuToggle, sidebarCollapsed }: HeaderProps) {
  // Same short-circuit as Sidebar — inside AppShell's Outlet, page-level
  // <Header /> instances render null so we don't stack two headers.
  if (useIsInsideAppShellChrome()) return null

  const [searchQuery, setSearchQuery] = useState("")
  const [isNotificationOpen, setIsNotificationOpen] = useState(false)
  const { isLoggedIn, isTenantUser, toggleChatPopup, unreadMessages } = useChat()

  // Notification poll (30s while tab focused). Tenant-only: super-admin
  // has no tenant-scoped notifications, so the poll must not fire on
  // the platform side (would 404/403 every 30s). Falls back to an empty
  // list + zero count on error / cold start.
  const { data: notificationsData } = useNotificationsPoll({
    enabled: isLoggedIn && isTenantUser,
    limit: 10,
  })
  const notifications = (notificationsData?.items ?? []).map((n) => ({
    id: n.id,
    title: n.title,
    description: n.message,
    time: relativeTime(n.createdAt),
    avatar: n.actor?.avatar || "/placeholder.svg?height=40&width=40",
    unread: !n.read,
  }))
  const unreadCount = notificationsData?.unreadCount ?? 0

  return (
    <header className="h-16 border-b border-divider bg-light-bg dark:bg-dark-bg flex items-center justify-between px-4">
      <div className="flex items-center gap-4">
        <Breadcrumbs />
      </div>

      <div className="flex items-center gap-4">
        {/* Global Search Field */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-light-text-secondary dark:text-dark-text-secondary" />
          <input
            type="text"
            placeholder="Search ⌘K"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 rounded-md bg-light-bg dark:bg-dark-bg text-light-text-primary dark:text-dark-text-primary placeholder:text-light-text-secondary dark:placeholder:text-dark-text-secondary border border-divider focus:outline-none focus:ring-1 focus:ring-primary w-64"
          />
        </div>

        {/* Theme Toggle */}
        <ThemeToggle />

        {/* Notification Icon */}
        <Popover open={isNotificationOpen} onOpenChange={setIsNotificationOpen}>
          <PopoverTrigger asChild>
            <button className="p-2 rounded-full hover:bg-primary-light relative">
              <Bell className="h-5 w-5 text-light-text-primary dark:text-dark-text-primary" />
              {unreadCount > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-error rounded-full"></span>}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-96 p-0 bg-light-card-bg dark:bg-dark-card-bg border-divider" align="end">
            <div className="p-4 border-b border-divider">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-light-text-primary dark:text-dark-text-primary">Notifications</h3>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-primary text-primary-foreground">
                    {unreadCount} New
                  </Badge>
                  <Mail className="h-4 w-4 text-light-text-secondary dark:text-dark-text-secondary" />
                </div>
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className="p-4 border-b border-divider hover:bg-light-table-header dark:hover:bg-dark-table-header transition-colors relative"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0">
                      {notification.avatar.startsWith("/") ? (
                        <Avatar className="h-10 w-10">
                          <AvatarImage src={notification.avatar || "/placeholder.svg"} />
                          <AvatarFallback>
                            {notification.title
                              .split(" ")
                              .map((n) => n[0])
                              .join("")}
                          </AvatarFallback>
                        </Avatar>
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                          {notification.avatar}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-light-text-primary dark:text-dark-text-primary">
                            {notification.title}
                          </p>
                          <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1">
                            {notification.description}
                          </p>
                          <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary mt-2">
                            {notification.time}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-2">
                          {notification.unread && <div className="w-2 h-2 bg-primary rounded-full"></div>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4">
              <Button className="w-full bg-primary hover:bg-primary/90 text-primary-foreground">
                View All Notifications
              </Button>
            </div>
          </PopoverContent>
        </Popover>

        {/* Chat Icon with Badge */}
        <button
          onClick={toggleChatPopup}
          className="p-2 rounded-full hover:bg-primary-light relative"
          aria-label="Toggle chat"
        >
          <ChatIconWithBadge unreadCount={unreadMessages} />
        </button>

        {/* User Profile Dropdown */}
        <UserProfileDropdown />
      </div>
    </header>
  )
}

// Default-export alias — several pages import this without braces.
export default Header

/**
 * Cheap "N minutes ago" / date fallback. Kept local to the header —
 * the notification row is the only place using it. Anything richer
 * (dedicated relative-time library, i18n) can layer on later.
 */
function relativeTime(iso?: string): string {
  if (!iso) return ""
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ""
  const diffSec = Math.round((Date.now() - then) / 1000)
  if (diffSec < 60) return "Just now"
  if (diffSec < 3600) return `${Math.round(diffSec / 60)}m ago`
  if (diffSec < 86400) return `${Math.round(diffSec / 3600)}h ago`
  if (diffSec < 604800) return `${Math.round(diffSec / 86400)}d ago`
  return new Date(then).toLocaleDateString(undefined, { month: "short", day: "numeric" })
}
